import { useEffect, useRef, useState } from 'react';

/**
 * Piccoli hook di servizio. Nessuna libreria di stato (decisione 20: poche
 * astrazioni, codice esplicito). Se in futuro servirà cache e invalidazione
 * si valuterà TanStack Query, non si costruirà un clone a mano.
 */

export type AsyncState<T> =
  | { readonly status: 'loading' }
  | { readonly status: 'success'; readonly data: T }
  | { readonly status: 'notFound' }
  | { readonly status: 'error'; readonly error: Error };

/**
 * Esegue una richiesta e ne espone lo stato. Quattro stati distinti, non tre:
 * `notFound` è separato da `error` perché in interfaccia sono cose diverse
 * (un comune che non esiste non è un guasto).
 *
 * `deps` va passato esplicitamente: nessuna magia sulla firma della funzione.
 */
export function useAsync<T>(
  fetcher: (signal: AbortSignal) => Promise<T | null>,
  deps: readonly unknown[],
): AsyncState<T> {
  const [state, setState] = useState<AsyncState<T>>({ status: 'loading' });

  useEffect(() => {
    const controller = new AbortController();
    setState({ status: 'loading' });

    fetcher(controller.signal)
      .then((data) => {
        if (controller.signal.aborted) return;
        setState(data === null ? { status: 'notFound' } : { status: 'success', data });
      })
      .catch((error: unknown) => {
        if (controller.signal.aborted) return;
        if (error instanceof DOMException && error.name === 'AbortError') return;
        setState({
          status: 'error',
          error: error instanceof Error ? error : new Error(String(error)),
        });
      });

    return () => controller.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  return state;
}

/**
 * Titolo del documento.
 *
 * In una SPA il `<title>` non cambia da sé: senza questo, tutte le 257 pagine
 * comune hanno lo stesso titolo nella cronologia, nei preferiti e negli
 * annunci dello screen reader (WCAG 2.4.2).
 */
export function useDocumentTitle(title: string | null): void {
  const previous = useRef<string>(document.title);

  useEffect(() => {
    if (!title) return;
    previous.current = document.title;
    document.title = `${title} · InfoComuni`;
    return () => {
      document.title = previous.current;
    };
  }, [title]);
}

/**
 * `<link rel="canonical">` della pagina corrente (§I.13, decisione 196).
 *
 * In una SPA lo stesso contenuto può rispondere a più indirizzi (rotta fissa
 * e `/p/<slug>/` per le pagine redazionali, slug e sigla per le province):
 * per un motore di ricerca è contenuto duplicato, e il canonico va DETTO.
 *
 * `percorso` è il percorso assoluto interno (`/legale/accessibilita/`);
 * l'href emesso è assoluto sull'origin corrente — la scelta del dominio
 * (www o apice) è del server, non di questo hook (docs/redirect-map.md,
 * «Da decidere» n. 3). Con `null` non si emette nulla: una pagina in
 * caricamento o inesistente non ha un canonico da dichiarare.
 */
export function useCanonical(percorso: string | null): void {
  useEffect(() => {
    if (!percorso) return;
    const link = document.createElement('link');
    link.rel = 'canonical';
    link.href = new URL(percorso, window.location.origin).toString();
    document.head.appendChild(link);
    return () => {
      link.remove();
    };
  }, [percorso]);
}
